import { useEffect, useRef, useState } from 'react';
import './App.css';

function initialPath() {
  if (/^\/(articles\/\d+|pubs\/(?:x\/)?\d+)\/?$/.test(window.location.pathname)) {
    return window.location.pathname.replace(/\/$/, '');
  }
  const parameter = window.WebApp?.initDataUnsafe?.start_param ?? '';
  const article = parameter.match(/^read_article_(\d+)$/);
  if (article) return `/articles/${article[1]}`;
  const publication = parameter.match(/^read_(?:(rss|x)_)?(\d+)$/);
  if (publication) return `/pubs/${publication[1] === 'x' ? 'x/' : ''}${publication[2]}`;
  return '/';
}

async function request(path, signal, options = {}) {
  const initData = window.WebApp?.initData;
  if (!initData) throw new Error('Откройте приложение внутри MAX, чтобы читать статьи.');
  const response = await fetch(`/api${path}`, { ...options, headers: { Authorization: initData }, signal, cache: 'no-store' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(response.status === 404 ? 'Статья не найдена.'
      : response.status === 401 ? 'Откройте миниапп заново из бота.' : 'Не удалось загрузить статьи. Попробуйте ещё раз.');
    error.code = body.detail?.code;
    error.botUrl = body.detail?.bot_url;
    throw error;
  }
  return response.json();
}

function Loader({ label = 'Загружаем статьи' }) {
  return <span className="loader" role="status"><span className="spinner" aria-hidden="true" /><span>{label}</span></span>;
}

function Header({ navigate }) {
  return (
    <header className="app-header">
      <div className="top-bar">
        <a className="network-mark" href="/" onClick={event => navigate(event, '/')} aria-label="TNN — все статьи">
          <img src="/assets/tnn_logo.jpg" alt="TNN" width="28" height="28" />
        </a>
        <div className="top-bar-stats" aria-label="91 источник, 49 стран, 26 языков">
          <span className="stat">
            <svg className="stat-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
              <rect x="2" y="3" width="12" height="10" rx="1" />
              <path d="M5 6h6M5 8.5h6M5 11h3M12 8.5v2.5" strokeLinecap="round" />
            </svg>
            <span className="stat-value">91</span><span className="stat-label">источник</span>
          </span>
          <span className="stat">
            <svg className="stat-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
              <path d="M8 14.5s-5-4.5-5-7.5a5 5 0 0 1 10 0c0 3-5 7.5-5 7.5z" strokeLinejoin="round" />
              <circle cx="8" cy="7" r="2" />
            </svg>
            <span className="stat-value">49</span><span className="stat-label">стран</span>
          </span>
          <span className="stat">
            <svg className="stat-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
              <circle cx="8" cy="8" r="6.5" />
              <path d="M1.5 8h13M2.5 4.5h11M2.5 11.5h11" strokeLinecap="round" />
              <ellipse cx="8" cy="8" rx="3" ry="6.5" />
            </svg>
            <span className="stat-value">26</span><span className="stat-label">языков</span>
          </span>
        </div>
      </div>
      <a className="masthead" href="/" onClick={event => navigate(event, '/')} aria-label="Говорит Планета — все статьи">
        <img src="/assets/planet_speaks_logo.jpg" alt="" width="48" height="48" />
        <span>Говорит <em>Планета</em></span>
      </a>
    </header>
  );
}

function Media({ media, href, navigate }) {
  if (!media?.url) return null;
  const image = <img src={media.url} alt="" loading="lazy" decoding="async" />;
  return (
    <figure className={`article-media${media.is_avatar ? ' account-portrait' : ''}`}>
      {media.type === 'image'
        ? (href ? <a className="cover-link" href={href} onClick={event => navigate(event, href)} aria-label="Открыть статью">{image}</a> : image)
        : <video src={media.url} controls playsInline preload="metadata" />}
      {media.caption && <figcaption dangerouslySetInnerHTML={{ __html: media.caption }} />}
    </figure>
  );
}

function ArticleHeading({ article, inList = false, navigate }) {
  const Heading = inList ? 'h2' : 'h1';
  return <>
    <Heading className="article-title">
      {inList ? <a href={article.url} onClick={event => navigate(event, article.url)}>{article.title}</a> : article.title}
    </Heading>
    <div className="article-source">
      <p>{article.source.label}</p>
      {article.source.position && <p className="source-position">{article.source.position}</p>}
    </div>
    <Media media={article.first_mediafile} href={inList ? article.url : undefined} navigate={navigate} />
    <time className="article-added" dateTime={article.added_iso}>{article.added}</time>
  </>;
}

export default function App() {
  const [path, setPath] = useState(initialPath);
  const [status, setStatus] = useState('loading');
  const [feed, setFeed] = useState({ items: [], page: 0, has_more: false, total: 0, loaded: false });
  const [article, setArticle] = useState(null);
  const [error, setError] = useState('');
  const [botUrl, setBotUrl] = useState('');
  const [retry, setRetry] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState('');
  const listScroll = useRef(0);
  const feedRef = useRef(feed);
  const moreController = useRef(null);

  useEffect(() => { feedRef.current = feed; }, [feed]);

  function showError(reason) {
    if (reason.name === 'AbortError') return;
    if (reason.code === 'bot_start_required') {
      setFeed({ items: [], page: 0, has_more: false, total: 0, loaded: false });
      feedRef.current = { loaded: false };
      setArticle(null);
      setBotUrl(reason.botUrl);
      setStatus('bot_required');
    } else {
      setError(reason.message);
      setStatus('error');
    }
  }

  useEffect(() => {
    window.history.replaceState(null, '', path);
    const onPop = () => setPath(window.location.pathname.replace(/\/$/, '') || '/');
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setError('');
    setMoreError('');
    setLoadingMore(false);
    if (path === '/' && feedRef.current.loaded) {
      setStatus('loading');
      request('/auth/', controller.signal, { method: 'POST' })
        .then(() => setStatus('list')).catch(showError);
      return () => controller.abort();
    }
    setStatus('loading');
    request(path === '/' ? '/articles/?page=1&page_size=10' : `${path}/`, controller.signal)
      .then(data => {
        if (path === '/') {
          setFeed({ ...data, loaded: true });
          setStatus('list');
        } else {
          setArticle(data);
          setStatus('article');
        }
      })
      .catch(showError);
    return () => controller.abort();
  }, [path, retry]);

  useEffect(() => {
    if (status === 'list') {
      document.title = 'Говорит Планета';
      window.scrollTo({ top: listScroll.current, behavior: 'instant' });
    } else if (status === 'article') {
      document.title = `${article.title} — Говорит Планета`;
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [status, article]);

  useEffect(() => () => moreController.current?.abort(), []);

  useEffect(() => {
    if (status !== 'list' && status !== 'article') return undefined;
    const controller = new AbortController();
    const checkAccess = () => {
      if (document.visibilityState === 'hidden') return;
      const query = article?.id && path !== '/' ? `?article_id=${article.id}` : '';
      request(`/auth/${query}`, controller.signal, { method: 'POST' }).catch(showError);
    };
    window.addEventListener('focus', checkAccess);
    window.addEventListener('pageshow', checkAccess);
    document.addEventListener('visibilitychange', checkAccess);
    return () => {
      controller.abort();
      window.removeEventListener('focus', checkAccess);
      window.removeEventListener('pageshow', checkAccess);
      document.removeEventListener('visibilitychange', checkAccess);
    };
  }, [status, path, article]);

  function navigate(event, href) {
    if (event && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    event?.preventDefault();
    if (path === href) {
      if (href === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (path === '/') listScroll.current = window.scrollY;
    moreController.current?.abort();
    window.history.pushState(null, '', href);
    setPath(href);
  }

  async function loadMore() {
    if (loadingMore || !feed.has_more) return;
    const controller = new AbortController();
    moreController.current = controller;
    setLoadingMore(true);
    setMoreError('');
    try {
      const data = await request(`/articles/?page=${feed.page + 1}&page_size=10`, controller.signal);
      setFeed(previous => {
        const known = new Set(previous.items.map(item => item.id));
        return { ...data, loaded: true, items: [...previous.items, ...data.items.filter(item => !known.has(item.id))] };
      });
    } catch (reason) {
      if (reason.code === 'bot_start_required') showError(reason);
      else if (reason.name !== 'AbortError') setMoreError(reason.message);
    } finally {
      if (!controller.signal.aborted) setLoadingMore(false);
    }
  }

  return (
    <div className="app">
      <Header navigate={navigate} />
      <main className="content" id="main-content" aria-busy={status === 'loading'}>
        {status === 'loading' && <div className="state-wrap"><Loader /></div>}
        {status === 'bot_required' && <section className="reader-gate" aria-labelledby="reader-gate-title">
          <span className="reader-gate-mark" aria-hidden="true">🌍</span>
          <h1 id="reader-gate-title">Продолжим в боте</h1>
          <p>Запустите «Говорит Планета» в MAX. Бот пришлёт сообщение с кнопкой, которая вернёт вас к чтению.</p>
          <p className="reader-gate-note">После запуска мы сможем присылать вам новые статьи и обновления в личный чат.</p>
          <a className="reader-gate-button" href={botUrl} onClick={event => {
            if (window.WebApp?.openMaxLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
              event.preventDefault();
              window.WebApp.openMaxLink(botUrl);
            }
          }}>👉🏻 Перейти в бота</a>
          <button className="text-button" onClick={() => setRetry(value => value + 1)}>Я уже нажал(а) «Старт» — проверить</button>
        </section>}
        {status === 'error' && <div className="state-wrap error-state" role="alert">
          <p>{error}</p>
          <button className="text-button" onClick={() => setRetry(value => value + 1)}>Попробовать снова <span aria-hidden="true">↻</span></button>
          {path !== '/' && <a className="back-link" href="/" onClick={event => navigate(event, '/')}>← Все статьи</a>}
        </div>}
        {status === 'list' && <section aria-labelledby="feed-title">
          <div className="section-heading"><h1 id="feed-title">Последние статьи</h1><span>Хроника событий</span></div>
          {feed.items.length === 0 && <div className="empty-state"><p>Истории ещё впереди.</p><span>Новые статьи появятся здесь.</span></div>}
          <ol className="pub-list">
            {feed.items.map(item => <li key={item.id}><article className="pub-item"><ArticleHeading article={item} inList navigate={navigate} /></article></li>)}
          </ol>
          {feed.has_more && <div className="load-more-wrap" aria-busy={loadingMore}>
            {moreError && <p className="more-error" role="alert">{moreError}</p>}
            <button className="load-more" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? <Loader label="Загружаем" /> : <>Показать больше <span aria-hidden="true">↓</span></>}
            </button>
          </div>}
          {feed.items.length > 0 && !feed.has_more && <p className="feed-end">Вы прочитали всю ленту</p>}
        </section>}
        {status === 'article' && article && <article className="article-page" key={article.id}>
          <a className="back-link" href="/" onClick={event => navigate(event, '/')}>← Все статьи</a>
          <ArticleHeading article={article} navigate={navigate} />
          <div className="article-body" dangerouslySetInnerHTML={{ __html: article.text }} />
          <div className="article-footer"><a className="back-link other-articles-link" href="/" onClick={event => navigate(event, '/')}>📚 Читать другие статьи</a></div>
        </article>}
      </main>
      <footer className="site-footer"><span>Говорит Планета</span></footer>
    </div>
  );
}
